import { rem, useToasts } from "@certn/cascadia";
import {
  Container,
  ContractScroll,
  Grid,
  GridItem,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { ActionBar } from "@certn/cascadia-react/components/action-bar/ActionBar";
import { PrimaryButton } from "@certn/cascadia-react/components/button/PrimaryButton";
import { useAPIRequest, usePeopleAPI } from "@certn/legacy-api";
import { css } from "@emotion/css";
import { useContext, useState, useRef, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

import constants from "../../../../../constants";
import { ApplicationContext } from "../../Application.context";
import { SectionProps } from "../types";

export function MouseSignatureSection({
  section,
  onNext,
  onBack,
}: SectionProps) {
  const { t } = useTranslation();
  const { applicantId } = useParams();
  const { show } = useToasts();
  const request = useAPIRequest();
  const [isLoading, setIsLoading] = useState(false);
  const { completeSection } = useContext(ApplicationContext);

  const [signed, setSigned] = useState(false);

  // Signature canvas ref
  const sigCanvas = useRef<SignatureCanvas>(null);

  // Clear signature function
  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  // Upload signature function
  const uploadSignature = async () => {
    if (sigCanvas.current?.isEmpty()) {
      show({
        title: t("Add your signature before continuing"),
        type: "error",
      });
      return;
    }

    const sigurl = sigCanvas.current?.toDataURL("image/png");

    try {
      await request(`/internal/people/applicant/${applicantId}/update/`, {
        method: "POST",
        body: JSON.stringify({ mouse_signature: sigurl }),
      });
    } catch (error) {
      console.error("Error uploading signature:", error);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  const handleNext = async () => {
    if (!signed) {
      show({
        title: t(
          "Consent to run a check is required in order to move forward."
        ),
        type: "error",
      });
    }
    // Check if signature is required and not provided
    if (sigCanvas.current?.isEmpty()) {
      show({
        title: t("Add your signature before continuing"),
        type: "error",
      });
      return;
    }

    await uploadSignature();
    await signContract();

    try {
      completeSection();
      onNext();
    } catch (e) {
      console.error("Error completing signature section:", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  const url =
    `/internal/people/applicant/${applicantId}/consent-and-disclosure-templates/` as "/internal/people/applicant/{id}/consent-and-disclosure-templates/";
  const { data: consents } = usePeopleAPI<typeof url>(url);

  const renderedData = useMemo(
    () =>
      consents?.reduce((i, consent) => ({ ...i, ...consent.render_data }), {}),
    [consents]
  );

  async function signContract() {
    try {
      await request(
        `/internal/people/applicant/${applicantId}/acknowledge-document/`,
        {
          method: "POST",
          body: JSON.stringify({
            render_data: consents?.[0].render_data,
            template_id: consents?.[0].template_id,
            version_id: "68f9f8de84ac20830554958d",
          }),
        }
      );
    } catch (e) {
      console.error("Error signing the document", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
    setSigned(true);
  }

  return (
    <Container>
      <Grid aria-label={t("Signature")} testID="signature-section-container">
        <GridItem alignItems="left">
          <Text
            as="h1"
            style="form/page-title"
            className={css({
              marginTop: rem("space-6"),
            })}
          >
            {t("Signature")}
          </Text>
          <Text
            style="form/body-primary"
            color="text-weak"
            className={css({ marginTop: rem("space-2") })}
          >
            <Trans>
              A signature is required for background check consent. Please
              provide your signature using your mouse or touch input before
              continuing.
            </Trans>
          </Text>
        </GridItem>

        <GridItem>
          <SectionMessage
            icon="verified_user"
            sectionTitle={t("Your privacy is important to us")}
            type="success"
          >
            <Trans>
              Certn will only collect information that is needed to complete
              your check.
            </Trans>
          </SectionMessage>
        </GridItem>

        <GridItem>
          {applicantId && (
            <ContractScroll
              accessID={constants.VITE_CLICKWRAP_ACCESS_ID}
              signerID={applicantId}
              index={1}
              contractIDs={["636992"]}
              isSigned={signed}
              onChange={signContract}
              renderData={renderedData}
              label={t("I understand and agree to this document")}
            />
          )}
        </GridItem>

        <GridItem>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: rem("space-4"),
              margin: `${rem("space-6")} 0`,
            })}
          >
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className: css({
                  border: "2px dashed #d9d9d9",
                  borderRadius: rem("space-2"),
                  background: "#fafafa",
                  cursor: "crosshair",
                }),
                width: 400,
                height: 200,
              }}
              data-testid="mouseSignatureBox"
            />

            <PrimaryButton
              testID="sig-clear"
              type="button"
              size="large"
              onPress={clearSignature}
              // className={css({ marginTop: rem("space-2") })}
            >
              {t("Clear")}
            </PrimaryButton>
          </div>
        </GridItem>

        <GridItem>
          <ActionBar
            primaryText={t("Next step")}
            onPrimaryClick={async () => {
              setIsLoading(true);

              try {
                await handleNext();
              } finally {
                setIsLoading(false);
              }
            }}
            backText={t("Back")}
            onBackClick={onBack}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </GridItem>
      </Grid>
    </Container>
  );
}
